export type SearchDropdownOutputMode = 'value' | 'valueWithSubtitle';

export type SearchDropdownVariant = 'default' | 'form';

export type SearchDropdownFilterConfig<T> = {
  items: T[];
  query: string;
  valuePath?: string;
  subtitlePath?: string;
  searchKeys?: string[];
  minChars?: number;
  maxResults?: number;
  getValue?: (item: T) => string;
  getSubtitle?: (item: T) => string;
  getSearchTexts?: (item: T) => string[];
};

export function readSearchPathText(item: unknown, path: string): string {
  if (!path) return '';

  const segments = String(path).replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  let current: unknown = item;

  for (const segment of segments) {
    if (current == null) return '';

    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return '';
      current = current[index];
      continue;
    }

    if (typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[segment];
  }

  if (Array.isArray(current)) {
    return current.map(value => String(value ?? '').trim()).filter(Boolean).join(' ');
  }

  return String(current ?? '').trim();
}

export function normalizeSearchText(value: unknown): string {
  return String(value ?? '').toLowerCase().trim();
}

export function formatSearchDropdownValue<T>(
  item: T,
  outputMode: SearchDropdownOutputMode,
  config: {
    valuePath?: string;
    subtitlePath?: string;
    getValue?: (item: T) => string;
    getSubtitle?: (item: T) => string;
    valueSeparator?: string;
  } = {}
): string {
  const value = config.getValue
    ? config.getValue(item)
    : readSearchPathText(item, config.valuePath || 'value');
  if (outputMode === 'value') return value;

  const subtitle = config.getSubtitle
    ? config.getSubtitle(item)
    : readSearchPathText(item, config.subtitlePath || 'subtitle');
  if (!subtitle) return value;

  const separator = config.valueSeparator ?? ': ';
  return `${value}${separator}${subtitle}`;
}

export function filterSearchDropdownItems<T>(config: SearchDropdownFilterConfig<T>): T[] {
  const {
    items,
    query,
    valuePath = 'value',
    subtitlePath,
    searchKeys,
    minChars = 2,
    maxResults = 8,
    getValue,
    getSubtitle,
    getSearchTexts
  } = config;

  const normalizedQuery = normalizeSearchText(query);
  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (normalizedQuery.length < minChars) return [];
  if (!tokens.length) {
    return items.slice(0, maxResults);
  }

  const keys = searchKeys?.length
    ? searchKeys
    : [valuePath, subtitlePath].filter((path): path is string => Boolean(path));

  return items
    .map(item => {
      const primaryText = normalizeSearchText(
        getValue ? getValue(item) : readSearchPathText(item, valuePath)
      );
      const haystacks = getSearchTexts
        ? getSearchTexts(item).map(normalizeSearchText).filter(Boolean)
        : keys.map(path => normalizeSearchText(readSearchPathText(item, path))).filter(Boolean);

      if (!haystacks.length) return null;
      if (!tokens.every(token => haystacks.some(value => value.includes(token)))) return null;

      let score = 300;
      if (primaryText === normalizedQuery) score = 0;
      else if (primaryText.startsWith(normalizedQuery)) score = 10;
      else if (haystacks.some(value => value === normalizedQuery)) score = 20;
      else if (haystacks.some(value => value.startsWith(normalizedQuery))) score = 40;
      else {
        score = 80 + tokens.reduce((total, token) => {
          const tokenScore = haystacks.reduce((best, value) => {
            const position = value.indexOf(token);
            return position < 0 ? best : Math.min(best, position);
          }, 999);
          return total + tokenScore;
        }, 0);
      }

      return { item, score };
    })
    .filter((result): result is { item: T; score: number } => result !== null)
    .sort((left, right) => left.score - right.score)
    .slice(0, maxResults)
    .map(result => result.item);
}

export function findSearchDropdownItem<T>(
  items: T[],
  value: string,
  config: {
    valuePath?: string;
    getValue?: (item: T) => string;
  } = {}
): T | undefined {
  const normalizedValue = normalizeSearchText(value);
  if (!normalizedValue) return undefined;

  return items.find(item => {
    const itemValue = config.getValue
      ? config.getValue(item)
      : readSearchPathText(item, config.valuePath || 'value');
    return normalizeSearchText(itemValue) === normalizedValue;
  });
}
